import html2canvas from "html2canvas";

type Rect = { left: number; top: number; width: number; height: number };

/**
 * CHP-809: the outcome of a region capture, as a discriminated result so the
 * caller can tell a genuine capture FAILURE (html2canvas threw, or a tainted
 * cross-origin canvas produced no blob) apart from a user CANCEL (Esc / no drag)
 * or a TOO-SMALL region. Previously all three resolved `null`, so a failed
 * snapshot silently no-opped and the reviewer thought they'd captured something.
 *
 *   - `{ ok: true,  blob }`            — a captured PNG region.
 *   - `{ ok: false, reason: "cancel" }`   — the reviewer dismissed capture.
 *   - `{ ok: false, reason: "too-small" }`— the drawn region was under the floor.
 *   - `{ ok: false, reason: "error" }`    — html2canvas failed / no blob produced.
 */
export type CaptureResult =
  { ok: true; blob: Blob } | { ok: false; reason: "cancel" | "too-small" | "error" };

/**
 * CHP-532: Coerce an arbitrary image Blob (an uploaded file or a pasted
 * clipboard image — JPEG, WebP, GIF, …) to a PNG Blob, because the screenshot
 * endpoint is PNG-only. A blob that is ALREADY image/png is returned as-is.
 * Anything else is decoded into an <img> and re-encoded onto a canvas as PNG.
 *
 * Resolves null if the blob isn't a decodable image (e.g. a non-image file the
 * picker let through, or a canvas that produced no output) so the caller can
 * surface a friendly error instead of uploading junk.
 */
export function ensurePng(blob: Blob): Promise<Blob | null> {
  if (blob.type === "image/png") return Promise.resolve(blob);
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth || img.width;
      canvas.height = img.naturalHeight || img.height;
      if (canvas.width === 0 || canvas.height === 0) {
        resolve(null);
        return;
      }
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(null);
        return;
      }
      ctx.drawImage(img, 0, 0);
      canvas.toBlob((b) => resolve(b), "image/png");
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

function normalize(x1: number, y1: number, x2: number, y2: number): Rect {
  return {
    left: Math.min(x1, x2),
    top: Math.min(y1, y2),
    width: Math.abs(x2 - x1),
    height: Math.abs(y2 - y1),
  };
}

/**
 * Let the reviewer drag a rectangle over the page, then capture just that region
 * as a PNG Blob via html2canvas. Resolves a {@link CaptureResult}: `cancel`
 * (Esc / click without drag), `too-small` (region under the floor), `error`
 * (html2canvas threw or produced no blob), or `ok` with the PNG blob.
 *
 * The caller is responsible for hiding its OWN UI (the comment panel + FAB)
 * before calling this so they aren't baked into the screenshot — this util only
 * manages its own transient draw layer.
 */
export function captureRegion(): Promise<CaptureResult> {
  return new Promise((resolve) => {
    const layer = document.createElement("div");
    layer.className = "rcmt-capture-layer";

    // CHP-805: announce capture mode — the layer/hint are otherwise silent,
    // non-interactive divs, so a screen-reader user gets no signal that the page
    // entered region-capture. role="status" + aria-live make the hint a polite
    // live region; the layer carries an accessible name.
    layer.setAttribute("aria-label", "Screenshot region capture");

    const hint = document.createElement("div");
    hint.className = "rcmt-capture-hint";
    hint.setAttribute("role", "status");
    hint.setAttribute("aria-live", "polite");
    hint.textContent = "Drag to select a region · Esc or Cancel to dismiss";

    // CHP-805: on-screen cancel affordance so capture mode can be exited without
    // a keyboard (touch/pen devices). pointer-events:auto (set in CSS) keeps it
    // tappable above the draw layer.
    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.className = "rcmt-capture-cancel";
    cancelBtn.textContent = "Cancel";

    const sel = document.createElement("div");
    sel.className = "rcmt-capture-sel";
    sel.style.display = "none";

    layer.appendChild(hint);
    layer.appendChild(cancelBtn);
    layer.appendChild(sel);
    document.body.appendChild(layer);

    // Keep an open app modal/drawer (Radix DismissableLayer) from closing when
    // the reviewer starts dragging over it. Radix dismisses on a document-level
    // `pointerdown` outside its content, and this capture layer is a body child
    // outside it — so without this, the very act of starting a region select
    // collapses the modal before html2canvas can snapshot it. Stop the layer's
    // touchstart from reaching that handler (capture phase, mirroring the
    // overlay container's guard).
    //
    // Regression fix: the *pointerdown* guard is deliberately NOT a separate
    // capture-phase listener. The layer is the pointerdown target, and a
    // capture-phase stopPropagation() on the target makes the dispatcher skip
    // the target's *bubble*-phase listeners. That silently killed the
    // drag-start handler once CHP-805 moved it from `mousedown` to
    // `pointerdown` (a `mousedown`-based drag was a different event, so it was
    // immune). Instead the drag-start handler below runs in the capture phase
    // and calls stopPropagation() itself — it both protects the modal and
    // actually starts the drag.
    const stopModalDismiss = (e: Event) => e.stopPropagation();
    layer.addEventListener("touchstart", stopModalDismiss, true);

    let dragging = false;
    let startX = 0;
    let startY = 0;
    let pointerId: number | null = null;

    const cleanup = () => {
      window.removeEventListener("keydown", onKey, true);
      layer.remove();
    };

    const cancel = () => {
      cleanup();
      resolve({ ok: false, reason: "cancel" });
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        cancel();
      }
    };
    window.addEventListener("keydown", onKey, true);
    cancelBtn.addEventListener("click", cancel);

    const drawSel = (cx: number, cy: number) => {
      const r = normalize(startX, startY, cx, cy);
      sel.style.left = `${r.left}px`;
      sel.style.top = `${r.top}px`;
      sel.style.width = `${r.width}px`;
      sel.style.height = `${r.height}px`;
    };

    // CHP-805: Pointer Events (not mouse-only) so the drag path covers mouse,
    // touch, and pen. setPointerCapture keeps move/up flowing to the layer even
    // if the pointer leaves it mid-drag.
    //
    // Regression fix: registered in the CAPTURE phase, and it does the modal-protecting
    // stopPropagation() itself. As a bubble-phase listener on the target layer
    // it would never run, because that same stopPropagation() (needed to keep an
    // open Radix modal from dismissing) is seen by the dispatcher before the
    // target's bubble phase — see the stopModalDismiss note above. pointermove /
    // pointerup stay in the bubble phase: nothing stops their propagation, and
    // setPointerCapture retargets them to the layer.
    layer.addEventListener(
      "pointerdown",
      (e) => {
        // Keep Radix's DismissableLayer (a document-level pointerdown handler)
        // from closing the modal when a drag begins over it.
        e.stopPropagation();
        // Only the bare layer initiates a drag — a press on the hint or the
        // Cancel button must not start a selection.
        if (e.target !== layer) return;
        dragging = true;
        pointerId = e.pointerId;
        try {
          layer.setPointerCapture(e.pointerId);
        } catch {
          // best-effort — drawing still works via the layer's own listeners
        }
        startX = e.clientX;
        startY = e.clientY;
        sel.style.display = "block";
        drawSel(e.clientX, e.clientY);
      },
      true,
    );
    layer.addEventListener("pointermove", (e) => {
      if (dragging) drawSel(e.clientX, e.clientY);
    });
    layer.addEventListener("pointerup", (e) => {
      if (!dragging) return;
      dragging = false;
      if (pointerId !== null) {
        try {
          layer.releasePointerCapture(pointerId);
        } catch {
          // capture may not have been set — ignore
        }
        pointerId = null;
      }
      const r = normalize(startX, startY, e.clientX, e.clientY);
      cleanup();
      if (r.width < 8 || r.height < 8) {
        resolve({ ok: false, reason: "too-small" });
        return;
      }
      // Two frames so the draw layer (and the caller-hidden panel) are gone from
      // the painted DOM before html2canvas snapshots the region.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          void html2canvas(document.body, {
            x: r.left + window.scrollX,
            y: r.top + window.scrollY,
            width: r.width,
            height: r.height,
            scale: window.devicePixelRatio || 1,
            useCORS: true,
            backgroundColor: null,
            logging: false,
          })
            // A tainted cross-origin canvas makes toBlob yield null — that's a
            // genuine failure (reason "error"), distinct from a cancel.
            .then((canvas) =>
              canvas.toBlob(
                (b) => resolve(b ? { ok: true, blob: b } : { ok: false, reason: "error" }),
                "image/png",
              ),
            )
            .catch(() => resolve({ ok: false, reason: "error" }));
        });
      });
    });
  });
}
